import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";
import { requestLocation } from "../lib/geolocation";

export default class extends Controller {
  static targets = [
    "canvas",
    "message",
    "recordingInterface",
    "initButton",
    "visualizerContainer",
    "loadingIndicator",
  ];

  connect() {
    console.log("Audio capture controller connected");
    this.recordingState = "idle"; // 'idle', 'ready', 'recording', 'processing'
    this.consumer = createConsumer();
    this.chunkSize = 64 * 1024; // 64KB chunks
  }

  disconnect() {
    this.stopVisualization();
    this.stopMicrophone();
  }

  canvasTargetConnected() {
    console.log("Canvas target connected");
    this.setupCanvas();
    // Don't auto-request microphone - wait for user gesture
    this.showRecordingInterface();
  }

  setupCanvas() {
    this.canvas = this.canvasTarget;
    this.canvasCtx = this.canvas.getContext("2d");
    this.WIDTH = this.canvas.width;
    this.HEIGHT = this.canvas.height;
    console.log("Canvas setup complete:", this.WIDTH, "x", this.HEIGHT);
  }

  async requestMicrophoneAccess() {
    try {
      console.log("Requesting microphone access");

      // Create audio context
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      // Resume audio context if suspended
      if (this.audioContext.state === "suspended") {
        console.log("Audio context suspended, resuming...");
        await this.audioContext.resume();
      }

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;

      console.log("Audio context state:", this.audioContext.state);

      // Get microphone stream
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log(
        "Microphone access granted, stream active:",
        this.stream.active,
      );

      // Connect microphone to analyser
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.source.connect(this.analyser);

      // Setup visualization data
      this.bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(this.bufferLength);

      console.log("Audio setup complete, buffer length:", this.bufferLength);

      this.hidePermissionMessage();
      this.showReadyState();
      this.startVisualization();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      this.showPermissionMessage();
    }
  }

  startVisualization() {
    if (this.isDrawing) return;
    console.log("Starting visualization");
    this.isDrawing = true;
    this.draw();
  }

  stopVisualization() {
    this.isDrawing = false;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  draw() {
    if (!this.isDrawing) return;

    this.animationFrame = requestAnimationFrame(this.draw.bind(this));

    // Get frequency data for visualization
    this.analyser.getByteFrequencyData(this.dataArray);

    // Clear canvas with state-based background
    const forced = document.documentElement.dataset.theme;
    const dark = forced
      ? forced === "dark"
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
    let backgroundColor, waveColor;
    if (this.recordingState === "recording") {
      backgroundColor = dark ? "#2a1519" : "#fef2f2";
      waveColor = dark ? "#f87171" : "#ef4444";
    } else {
      backgroundColor = dark ? "#1c2230" : "#f9fafb";
      waveColor = dark ? "#34d399" : "#10b981";
    }

    this.canvasCtx.fillStyle = backgroundColor;
    this.canvasCtx.fillRect(0, 0, this.WIDTH, this.HEIGHT);

    // Draw horizontal waveform
    this.canvasCtx.beginPath();
    this.canvasCtx.strokeStyle = waveColor;
    this.canvasCtx.lineWidth = 2;

    const centerY = this.HEIGHT / 2;
    const barWidth = this.WIDTH / this.bufferLength;

    for (let i = 0; i < this.bufferLength; i++) {
      const barHeight = (this.dataArray[i] / 255) * (this.HEIGHT / 2);
      const x = i * barWidth;

      if (i === 0) {
        this.canvasCtx.moveTo(x, centerY - barHeight / 2);
      } else {
        this.canvasCtx.lineTo(x, centerY - barHeight / 2);
      }
    }

    this.canvasCtx.stroke();
  }

  stopMicrophone() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.stopVisualization();
  }

  // Initialize button click handler
  async initialize() {
    console.log("Initialize button clicked");

    // Always try to resume audio context on user interaction
    if (this.audioContext && this.audioContext.state === "suspended") {
      console.log("Resuming audio context on user click");
      await this.audioContext.resume();
    }

    if (!this.stream) {
      this.requestMicrophoneAccess();
    } else if (!this.isDrawing) {
      // Restart visualization if it stopped
      this.startVisualization();
    }
  }

  // Canvas click handler for toggling recording
  toggleRecording() {
    console.log("Canvas clicked, current state:", this.recordingState);

    if (this.recordingState === "ready") {
      this.startRecording();
    } else if (this.recordingState === "recording") {
      this.stopRecording();
    }
  }

  startRecording() {
    console.log("Starting recording");
    this.recordingState = "recording";

    // Prevent modal from closing during recording
    this.preventModalClose();

    // Set up MediaRecorder
    this.setupMediaRecorder();

    if (!this.mediaRecorder) {
      console.error("Failed to set up MediaRecorder");
      return;
    }

    // Initialize ActionCable subscription
    this.initializeActionCable();

    // The fix can arrive after recording has started, so it is sent on its own
    requestLocation((location) =>
      this.subscription?.perform("location", location),
    );

    // Start recording
    this.mediaRecorder.start(1000); // Send chunks every 1000ms
  }

  stopRecording() {
    console.log("Stopping recording");
    this.recordingState = "processing";
    this.showLoadingState();

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
    }
  }

  setupMediaRecorder() {
    if (!this.stream) {
      console.error("No audio stream available");
      return false;
    }

    // Try different mime types for browser compatibility
    const options = { mimeType: "audio/webm" };
    try {
      this.mediaRecorder = new MediaRecorder(this.stream, options);
    } catch (e1) {
      try {
        this.mediaRecorder = new MediaRecorder(this.stream);
      } catch (e2) {
        console.error("MediaRecorder not supported", e2);
        return false;
      }
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0 && this.subscription) {
        this.sendAudioChunk(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      console.log("MediaRecorder stopped, sending end of stream");
      if (this.subscription) {
        setTimeout(() => {
          this.subscription.send({ end_of_stream: true });
        }, 1500);
      }
    };

    return true;
  }

  async sendAudioChunk(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < uint8Array.length; i += this.chunkSize) {
      const chunk = uint8Array.slice(i, i + this.chunkSize);
      const base64data = btoa(String.fromCharCode.apply(null, chunk));
      this.subscription.send({
        audio_chunk: base64data,
      });
    }
  }

  initializeActionCable() {
    // Get song_id from the form input
    const songId = this.element.querySelector(
      '[data-audio-capture-target="recordingSongId"]',
    )?.value;

    this.subscription = this.consumer.subscriptions.create(
      {
        channel: "AudioChannel",
        song_id: songId,
      },
      {
        connected: () => {
          console.log("Connected to AudioChannel");
        },
        disconnected: () => {
          console.log("Disconnected from AudioChannel");
        },
        received: (data) => {
          console.log("Received from AudioChannel:", data);
          if (data.status === "completed") {
            this.handleRecordingCompleted(data);
          } else if (data.status === "failed") {
            this.handleRecordingFailed(data);
          }
        },
      },
    );
  }

  handleRecordingCompleted(data) {
    console.log("Recording completed:", data);

    // Clean up subscription
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    // Reset state
    this.recordingState = "idle";

    // Allow modal to close again
    this.allowModalClose();

    // Dispatch event for recording interface controller to handle
    const event = new CustomEvent("audio-capture:completed", {
      detail: {
        recordingId: data.recording_id,
        audioUrl: data.audio_url,
      },
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }

  handleRecordingFailed(data) {
    console.error("Recording failed:", data);
    this.recordingState = "ready";

    // Allow modal to close again
    this.allowModalClose();

    // Clean up subscription
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    // TODO: Show error message to user
    alert("Recording failed: " + data.error);
  }

  showReadyState() {
    console.log("Showing ready state - hiding button, showing visualizer");
    this.recordingState = "ready";

    // Hide initialize button and show visualizer
    if (this.hasInitButtonTarget) {
      this.initButtonTarget.classList.add("hidden");
      console.log("Hidden initialize button");
    } else {
      console.log("No init button target found");
    }
    if (this.hasVisualizerContainerTarget) {
      this.visualizerContainerTarget.classList.remove("hidden");
      console.log("Shown visualizer container");
    } else {
      console.log("No visualizer container target found");
    }
  }

  showPermissionMessage() {
    // Hide everything else and show permission message
    if (this.hasInitButtonTarget) {
      this.initButtonTarget.classList.add("hidden");
    }
    if (this.hasVisualizerContainerTarget) {
      this.visualizerContainerTarget.classList.add("hidden");
    }
    if (this.hasRecordingInterfaceTarget) {
      this.recordingInterfaceTarget.classList.add("hidden");
    }
    if (this.hasMessageTarget) {
      this.messageTarget.classList.remove("hidden");
    }
  }

  hidePermissionMessage() {
    if (this.hasMessageTarget) {
      this.messageTarget.classList.add("hidden");
    }
    if (this.hasRecordingInterfaceTarget) {
      this.recordingInterfaceTarget.classList.remove("hidden");
    }
  }

  showRecordingInterface() {
    if (this.hasRecordingInterfaceTarget) {
      this.recordingInterfaceTarget.classList.remove("hidden");
    }
  }

  showLoadingState() {
    // Hide visualizer and show loading indicator
    if (this.hasVisualizerContainerTarget) {
      this.visualizerContainerTarget.classList.add("hidden");
    }
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove("hidden");
    }
    this.stopVisualization();
  }

  // Modal communication methods
  preventModalClose() {
    const modalController = this.getModalController();
    if (modalController) {
      modalController.preventClose();
    }
  }

  allowModalClose() {
    const modalController = this.getModalController();
    if (modalController) {
      modalController.allowClose();
    }
  }

  getModalController() {
    // Find the modal controller in the DOM hierarchy
    const modalElement = this.element.closest('[data-controller*="modal"]');
    if (modalElement) {
      return this.application.getControllerForElementAndIdentifier(
        modalElement,
        "modal",
      );
    }
    return null;
  }

  // Reset the audio capture interface to initial state
  reset() {
    console.log("Resetting audio capture interface");

    // Stop all audio operations
    this.stopVisualization();
    this.stopMicrophone();

    // Clean up subscriptions
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    // Reset recording state
    this.recordingState = "idle";

    // Reset UI to initial state
    this.showInitialState();
  }

  showInitialState() {
    // Show initialize button, hide everything else
    if (this.hasInitButtonTarget) {
      this.initButtonTarget.classList.remove("hidden");
    }
    if (this.hasVisualizerContainerTarget) {
      this.visualizerContainerTarget.classList.add("hidden");
    }
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden");
    }
    if (this.hasMessageTarget) {
      this.messageTarget.classList.add("hidden");
    }
  }
}
